import { useEffect, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { TrendingUp, Users, ShoppingBag, Percent, Wallet, PieChart as PieIcon, RefreshCw, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { readError } from "../api";
import { money } from "../studioUtils";
import { ApprovalAnalytics } from "../components/ApprovalAnalytics";

const STATUS_COLORS = {
  taslak: "#94a3b8", gonderildi: "#3b82f6", gorusuluyor: "#f59e0b",
  onaylandi: "#059669", reddedildi: "#dc2626", beklemede: "#d97706",
};
const STATUS_LABEL = {
  taslak: "Taslak", gonderildi: "Gönderildi", gorusuluyor: "Görüşülüyor",
  onaylandi: "Onaylandı", reddedildi: "Reddedildi", beklemede: "Beklemede",
};

const monthLabel = (ym) => {
  const [y, m] = (ym || "").split("-");
  if (!y || !m) return ym;
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("tr-TR", { month: "short", year: "2-digit" });
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      setData(data);
    } catch (err) { toast.error(readError(err, "İstatistikler yüklenemedi")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="empty-state"><BarChart3 size={26} /><h2>Yükleniyor…</h2></div>;

  const t = data.totals;
  const monthly = (data.monthly || []).map(m => ({ ...m, label: monthLabel(m.month) }));
  const pie = (data.status_pie || []).map(s => ({ ...s, label: STATUS_LABEL[s.status] || s.status,
                                                    color: STATUS_COLORS[s.status] || "#64748b" }));

  return (
    <section className="dashboard-view" data-testid="dashboard-view">
      <div className="dashboard-head">
        <div>
          <p className="eyebrow blue">KONTROL PANELİ</p>
          <h2>Satış özet</h2>
          <p>Son 6 ayın performansı, en çok satan ürünler ve müşteriler.</p>
        </div>
        <button className="outline-button" onClick={load} disabled={busy} data-testid="dashboard-refresh">
          <RefreshCw size={14} /> {busy ? "Yenileniyor..." : "Yenile"}
        </button>
      </div>

      <div className="stat-cards">
        <StatCard icon={<ShoppingBag size={18} />} label="Toplam teklif" value={t.quote_count} testid="stat-count" />
        <StatCard icon={<TrendingUp size={18} />} label="Onaylanan" value={t.approved_count} testid="stat-approved" />
        <StatCard icon={<Percent size={18} />} label="Dönüşüm oranı" value={`% ${t.conversion_rate}`} testid="stat-conversion" />
        <StatCard icon={<Wallet size={18} />} label="Toplam ciro" value={money(t.revenue_total)} testid="stat-revenue" wide />
        <StatCard icon={<Users size={18} />} label="Ortalama sepet" value={money(t.avg_basket)} testid="stat-avg-basket" />
      </div>

      <ApprovalAnalytics refreshKey={data} />

      <div className="chart-grid">
        <div className="chart-card" data-testid="chart-monthly">
          <div className="chart-head"><b>Aylık ciro & teklif adedi</b><span>Son 6 ay</span></div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip formatter={(v, k) => (k === "total" ? money(v) : v)} />
                <Legend />
                <Bar yAxisId="left" dataKey="total" name="Ciro (₺)" fill="#2563eb" radius={[6, 6, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="count" name="Teklif adedi" stroke="#f59e0b" strokeWidth={2} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card" data-testid="chart-status">
          <div className="chart-head"><b><PieIcon size={14} /> Durum dağılımı</b><span>Tüm teklifler</span></div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pie} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={95} innerRadius={55} label>
                  {pie.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-card" data-testid="chart-products">
          <div className="chart-head"><b>En çok satan 5 ürün</b><span>Ciro bazında</span></div>
          <div className="chart-body dashboard-table">
            {(data.top_products || []).length === 0 ? (
              <p className="muted">Henüz veri yok.</p>
            ) : (data.top_products || []).map((p, i) => (
              <div className="dashboard-row" key={i} data-testid={`top-product-${i}`}>
                <span className="rank">#{i + 1}</span>
                <span className="name">{p.name}</span>
                <span className="meta">{p.count} teklif · {p.qty.toFixed(0)} adet</span>
                <b>{money(p.revenue)}</b>
              </div>
            ))}
          </div>
        </div>
        <div className="chart-card" data-testid="chart-customers">
          <div className="chart-head"><b>En büyük 5 müşteri</b><span>Toplam teklif tutarına göre</span></div>
          <div className="chart-body dashboard-table">
            {(data.top_customers || []).length === 0 ? (
              <p className="muted">Henüz veri yok.</p>
            ) : (data.top_customers || []).map((c, i) => (
              <div className="dashboard-row" key={i} data-testid={`top-customer-${i}`}>
                <span className="rank">#{i + 1}</span>
                <span className="name">{c.name}</span>
                <span className="meta">{c.count} teklif</span>
                <b>{money(c.total)}</b>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon, label, value, testid, wide }) {
  return (
    <div className={`stat-card ${wide ? "wide" : ""}`} data-testid={testid}>
      <span className="stat-icon">{icon}</span>
      <div>
        <span className="stat-label">{label}</span>
        <strong className="stat-value">{value}</strong>
      </div>
    </div>
  );
}
